import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { 
  Loader2, Save, Trash2, Check, MapPin, Phone, Mail, Globe,
  AlertTriangle, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API } from '@/utils/api';

export default function EditBreederPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const flow = searchParams.get('flow'); // 'miot' | 'reproduktor' | null
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    axios.get(`${API}/public/breeder-edit/${token}`).then(res => {
      const s = res.data.submission;
      setData(s);
      setForm({
        kennel_name: s.kennel_name || '',
        breeds: (s.breeds || []).join(', '),
        city: s.city || '',
        voivodeship: s.voivodeship || '',
        phone: s.phone || '',
        email: s.email || '',
        description: s.description || '',
        website: s.website || '',
        facebook: s.facebook || '',
        instagram: s.instagram || '',
      });
    }).catch(err => {
      setError(err.response?.data?.detail || 'Nie znaleziono wpisu');
    }).finally(() => setLoading(false));
  }, [token]);

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, breeds: form.breeds.split(',').map(b => b.trim()).filter(Boolean) };
      await axios.put(`${API}/public/breeder-edit/${token}`, payload);
      toast.success('Zmiany zapisane!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd zapisu');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Na pewno usunąć hodowlę z katalogu? Tej operacji nie można cofnąć.')) return;
    try {
      await axios.delete(`${API}/public/breeder-edit/${token}`);
      setDeleted(true);
    } catch (err) {
      toast.error('Błąd usuwania');
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center px-6">
      <div className="text-center">
        <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Nie znaleziono wpisu</h1>
        <p className="text-gray-400 mb-6">{error}</p>
        <Link to="/dodaj-hodowle"><Button className="bg-blue-500 hover:bg-blue-600 rounded-full px-6">Dodaj nową hodowlę</Button></Link>
      </div>
    </div>
  );

  if (deleted) return (
    <div className="min-h-screen bg-[#0B0F1A] flex items-center justify-center px-6">
      <div className="text-center">
        <Check className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Hodowla usunięta</h1>
        <p className="text-gray-400 mb-6">Wpis został usunięty z katalogu.</p>
        <Link to="/"><Button className="bg-blue-500 hover:bg-blue-600 rounded-full px-6">Strona główna</Button></Link>
      </div>
    </div>
  );

  return (
    <>
      <Helmet><title>Edytuj hodowlę | ApisAI</title></Helmet>
      <div className="min-h-screen bg-[#0B0F1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0F1A]/80 backdrop-blur-xl border-b border-white/5">
          <div className="max-w-lg mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.svg" alt="ApisAI" className="w-9 h-9" />
              <span className="font-semibold text-lg text-white">ApisAI</span>
            </Link>
          </div>
        </nav>

        <div className="pt-28 pb-20 px-4">
          <div className="max-w-lg mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-white mb-2" data-testid="edit-title">Edytuj hodowlę</h1>
                <p className="text-gray-400 text-sm">{data?.kennel_name} — {data?.city}</p>
                <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${data?.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  {data?.status === 'active' ? 'Aktywna' : 'Oczekuje na weryfikację'}
                </span>
              </div>

              {/* FLOW CONTINUATION BANNER (P0) */}
              {flow === 'miot' && (
                <div className="mb-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5" data-testid="flow-continuation-miot">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-500 text-black flex items-center justify-center font-bold shrink-0">2</div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-emerald-200 mb-1">Krok 2: dodaj szczenięta</h3>
                      <p className="text-sm text-white/75 mb-3">
                        Hodowla zapisana. Zaloguj się lub załóż konto hodowcy, aby dodać miot. Wszystko zajmie minutę.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/rejestracja?submission=${token}&flow=miot`}>
                          <Button className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-10 px-5" data-testid="flow-register-cta">
                            Załóż konto i dodaj szczenięta <ArrowRight className="w-4 h-4 ml-1.5" />
                          </Button>
                        </Link>
                        <Link to={`/login?redirect=/wizard/miot`}>
                          <Button variant="outline" className="rounded-full border-white/15 bg-white/[0.04] text-white hover:bg-white/10 h-10 px-5">
                            Mam już konto
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {flow === 'reproduktor' && (
                <div className="mb-6 rounded-2xl border border-purple-500/30 bg-purple-500/10 p-5" data-testid="flow-continuation-stud">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold shrink-0">2</div>
                    <div className="flex-1">
                      <h3 className="text-base font-bold text-purple-200 mb-1">Krok 2: dodaj reproduktora</h3>
                      <p className="text-sm text-white/75 mb-3">
                        Hodowla zapisana. Zaloguj się lub załóż konto hodowcy, aby dodać reproduktora.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/rejestracja?submission=${token}&flow=reproduktor`}>
                          <Button className="rounded-full bg-purple-500 hover:bg-purple-400 text-white font-semibold h-10 px-5" data-testid="flow-register-cta">
                            Załóż konto i dodaj reproduktora <ArrowRight className="w-4 h-4 ml-1.5" />
                          </Button>
                        </Link>
                        <Link to={`/login?redirect=/wizard/reproduktor`}>
                          <Button variant="outline" className="rounded-full border-white/15 bg-white/[0.04] text-white hover:bg-white/10 h-10 px-5">
                            Mam już konto
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 mb-1.5 block">Nazwa hodowli</label>
                  <input value={form.kennel_name} onChange={e => update('kennel_name', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50" data-testid="edit-name" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 mb-1.5 block">Rasy (oddzielone przecinkami)</label>
                  <input value={form.breeds} onChange={e => update('breeds', e.target.value)}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Miasto</label>
                    <input value={form.city} onChange={e => update('city', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Województwo</label>
                    <input value={form.voivodeship} onChange={e => update('voivodeship', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Telefon</label>
                    <input value={form.phone} onChange={e => update('phone', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-400 mb-1.5 block">Email</label>
                    <input value={form.email} onChange={e => update('email', e.target.value)}
                      className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 mb-1.5 block">Opis</label>
                  <textarea value={form.description} onChange={e => update('description', e.target.value)} rows={3}
                    className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50 resize-none" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <input value={form.website} onChange={e => update('website', e.target.value)} placeholder="Strona www"
                    className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50 placeholder-gray-500" />
                  <input value={form.facebook} onChange={e => update('facebook', e.target.value)} placeholder="Facebook"
                    className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50 placeholder-gray-500" />
                  <input value={form.instagram} onChange={e => update('instagram', e.target.value)} placeholder="Instagram"
                    className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-blue-500/50 placeholder-gray-500" />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSave} disabled={saving} className="flex-1 bg-blue-500 hover:bg-blue-600 rounded-xl" data-testid="save-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Zapisz zmiany
                  </Button>
                  <Button onClick={handleDelete} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-xl" data-testid="delete-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Usuń
                  </Button>
                </div>

                <p className="text-center text-gray-500 text-xs mt-4">
                  Zapisz ten link — to Twój klucz do edycji wpisu.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
